import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { program } from "@/api";
import { TIER_META } from "@/constants";
import { ArrowRight, Sparkles, Leaf, CheckCircle2, Zap, Handshake, ShieldCheck, Coins } from "lucide-react";

const PILLAR_ICONS = {
  calidad: ShieldCheck,
  costo: Coins,
  continuidad: CheckCircle2,
  innovacion: Sparkles,
  sustentabilidad: Leaf,
  relacion: Handshake,
};

const PILLAR_ICON_IMG = {
  calidad: "/brand/icon_pilar_calidad.png",
  costo: "/brand/icon_pilar_costo.png",
  continuidad: "/brand/icon_pilar_continuidad.png",
  innovacion: "/brand/icon_pilar_innovacion.png",
  sustentabilidad: "/brand/icon_pilar_sustentabilidad.png",
  relacion: "/brand/icon_pilar_servicio.png",
};

const PILLAR_BLURBS = {
  calidad: "Cuidamos juntos lo que llega a millones de familias: especificaciones, certificaciones y procesos sin sorpresas.",
  costo: "Construimos competitividad sostenible: precio justo, transparencia y eficiencias compartidas.",
  continuidad: "Estamos listos pase lo que pase: entregas a tiempo, capacidad disponible y planes ante imprevistos.",
  innovacion: "Avanzamos juntos: propuestas, mejoras y desarrollo conjunto que llevan a Lala más allá.",
  sustentabilidad: "Cuidamos el planeta como cuidamos lo nuestro: huella ambiental, prácticas regenerativas y evidencia ESG.",
  relacion: "Crecemos como socios: comunicación abierta, cumplimiento normativo y compromiso de largo plazo.",
};

const TIER_FRAME = { partner: "card-platinum", key: "card-gold", challenge: "card-silver", commercial: "card-bronze" };

// Video incluido en el build: respaldo cuando el configurado por el admin no se puede cargar.
const DEFAULT_HERO_VIDEO = { hero_video_url: "/brand/hero_video.mp4", hero_poster_url: "/brand/hero_video_poster.jpg" };

export default function Landing() {
  const [data, setData] = useState(null);
  const [heroVideo, setHeroVideo] = useState(DEFAULT_HERO_VIDEO);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/platform-videos`)
      .then((r) => r.json()).then(setHeroVideo).catch(() => {});
  }, []);

  useEffect(() => {
    program.publicInfo().then(setData).catch(() => {});
  }, []);

  return (
    <div className="paper min-h-screen">
      {/* Hero — fondo azul Lala + mariposa grande · tipografía blanca */}
      <section
        className="relative overflow-hidden"
        style={{
          backgroundColor: "var(--lala-blue)",
          backgroundImage: "url(/brand/mariposa_azul_bg.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-6 pt-14 pb-10 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 rise rise-1">
            <div className="font-hand text-2xl mb-3" style={{ color: "#fff", opacity: 0.9 }} data-testid="hero-eyebrow">
              comunidad de proveedores estratégicos de Grupo Lala
            </div>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] font-semibold text-white" data-testid="hero-title">
              Construidos<br />
              para crecer juntos.
            </h1>
            <p className="mt-6 text-lg md:text-xl max-w-xl text-white/90 leading-relaxed">
              <strong className="text-white">LALA Elite</strong> es la red de colaboración y confianza
              compartida entre Grupo Lala y sus proveedores estratégicos. Una comunidad que impulsa estándares más
              altos y evolución constante — porque cuando todos avanzamos juntos, el impacto se expande hacia
              millones de familias.
            </p>
            <p className="mt-3 text-sm text-white/75 max-w-xl">
              No es una calificación: es un mapa de crecimiento mutuo. Aquí cada proveedor entiende dónde está,
              hacia dónde va y cómo seguimos avanzando como familia Lala.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-ctas">
              <Link to="/login" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[var(--lala-blue)] font-bold hover:translate-y-[-2px] transition-transform">
                Acceso proveedores y Lala <ArrowRight size={18} />
              </Link>
              <a href="#pilares" className="inline-flex items-center px-5 py-3 rounded-full border border-white/60 text-white font-semibold hover:bg-white/10 transition-colors">Conoce el programa</a>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-white/80" data-testid="hero-stats">
              <div><strong className="text-xl font-display" style={{ color: "var(--lala-celeste)" }}>360°</strong><div>evaluación integral</div></div>
              <div className="w-px h-10 bg-white/30" />
              <div><strong className="text-xl font-display" style={{ color: "var(--lala-celeste)" }}>6</strong><div>pilares compartidos</div></div>
              <div className="w-px h-10 bg-white/30" />
              <div><strong className="text-xl font-display" style={{ color: "var(--lala-celeste)" }}>4</strong><div>etapas de crecimiento</div></div>
            </div>
          </div>
          <div className="md:col-span-5 relative rise rise-2">
            <div className="font-hand text-2xl mb-3" style={{ color: "#fff", opacity: 0.9 }}>construidos para crecer juntos</div>
            <div className="card-silver p-4 pt-10 relative mt-14" data-testid="hero-tier-card">
              <img
                src="/brand/lala_elite_badge_md.png"
                alt="Lala Elite"
                data-testid="hero-estrella-brillo"
                className="absolute -top-14 left-1/2 -translate-x-1/2 w-28 h-28 object-contain float-y drop-shadow-2xl z-10"
                draggable={false}
              />
              <video
                src={heroVideo.hero_video_url}
                poster={heroVideo.hero_poster_url}
                autoPlay
                muted
                loop
                playsInline
                controls
                className="w-full rounded-xl block"
                data-testid="hero-video"
                onError={() => setHeroVideo((current) =>
                  current.hero_video_url === DEFAULT_HERO_VIDEO.hero_video_url ? current : DEFAULT_HERO_VIDEO)}
              />
              <div className="text-center font-display text-2xl text-[var(--lala-blue)] mt-3">la estrella en su mejor brillo</div>
              <div className="text-center text-sm text-[var(--ink-soft)] mt-1">símbolo del proveedor que alcanzó su máximo vuelo.</div>
            </div>
          </div>
        </div>
        {/* Logo LALA institucional — jerarquía prioritaria, abajo y centrado */}
        <div className="relative pb-8 flex justify-center">
          <img
            src="/brand/lala_blanco_trim.png"
            alt="Grupo LALA"
            className="h-16 w-auto"
            data-testid="hero-lala-logo"
            draggable={false}
          />
        </div>
      </section>

      {/* Journey — Evaluamos → Desarrollamos → Evolucionamos → Reconocemos */}
      {data?.narrative?.journey?.length > 0 && (
        <section id="journey" className="py-16 border-b border-[var(--line)]" style={{ backgroundColor: "#fff", backgroundImage: "url(/brand/textura_leche.png)", backgroundSize: "cover", backgroundPosition: "center" }} data-testid="program-journey">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-10">
              <div className="font-hand text-xl text-[var(--lala-blue)]">así trabajamos contigo</div>
              <h2 className="font-display text-4xl md:text-5xl mt-1 text-[var(--lala-blue)]">{data.narrative.headline || "Evaluamos hoy para construir el futuro de Lala"}</h2>
              <p className="text-[var(--ink-soft)] mt-4 leading-relaxed">{data.narrative.subhead}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="journey-grid">
              {data.narrative.journey.map((j, i) => (
                <div key={j.step} className="sketch-border bg-white p-5 relative" data-testid={`journey-step-${j.step.toLowerCase()}`}>
                  <div className="absolute -top-3 left-4 px-2 py-0.5 rounded-full bg-[var(--lala-blue)] text-white text-[10px] uppercase tracking-widest font-bold">paso {i + 1}</div>
                  <div className="font-display text-2xl text-[var(--lala-blue)] mt-2">{j.step}</div>
                  <p className="text-sm text-[var(--ink-soft)] mt-2 leading-relaxed">{j.description}</p>
                </div>
              ))}
            </div>
            {data.narrative.purpose && (
              <p className="text-center text-base text-[var(--ink)] mt-10 max-w-3xl mx-auto leading-relaxed">
                <span className="font-hand text-xl text-[var(--lala-blue)]">el objetivo:</span><br />{data.narrative.purpose}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Pilares — fondo blanco en continuidad · títulos azul Lala */}
      <section id="pilares" className="border-b border-[var(--line)] py-20" style={{ backgroundColor: "#fff", backgroundImage: "url(/brand/textura_leche.png)", backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="font-hand text-xl text-[var(--lala-blue)]">seis pilares · una sola familia</div>
              <h2 className="font-display text-4xl md:text-5xl mt-1 text-[var(--lala-blue)]">Avanzamos juntos en seis frentes que importan a millones.</h2>
            </div>
            <div className="font-hand text-2xl text-[var(--lala-blue)] hidden md:block">Crecimiento 360°</div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pillars-grid">
            {(data?.pillars || []).map((p, i) => {
              const Icon = PILLAR_ICONS[p.key] || Zap;
              return (
                <div key={p.key} className={`sketch-border p-6 bg-white rise rise-${(i % 5) + 1}`} data-testid={`pillar-card-${p.key}`}>
                  <div className="flex items-start justify-between mb-3">
                    {PILLAR_ICON_IMG[p.key] ? (
                      <img src={PILLAR_ICON_IMG[p.key]} alt="" className="w-12 h-12 object-contain" data-testid={`pillar-icon-${p.key}`} draggable={false} />
                    ) : (
                      <span className="w-12 h-12 rounded-full flex items-center justify-center bg-[var(--lala-blue)]">
                        <Icon size={24} className="text-white" />
                      </span>
                    )}
                    <div className="text-right">
                      <div className="font-hand text-2xl text-[var(--lala-blue)]">0{i + 1}</div>
                      {p.weight != null && <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)] font-bold">peso {p.weight}%</div>}
                    </div>
                  </div>
                  <div className="font-display text-2xl font-semibold text-[var(--lala-blue)]">{p.label}</div>
                  {p.tagline && <div className="text-xs text-[var(--lala-blue)] font-semibold mt-1">{p.tagline}</div>}
                  <p className="text-sm text-[var(--ink-soft)] mt-2 leading-relaxed">{p.description || PILLAR_BLURBS[p.key]}</p>
                  {p.campaign_phrase && (
                    <div className="mt-3 pt-3 border-t border-[var(--line)]">
                      <div className="font-hand text-base text-[var(--lala-blue)] leading-tight" data-testid={`pillar-phrase-${p.key}`}>
                        “{p.campaign_phrase}”
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tiers — marcos metálicos · títulos Bolota · iconos homologados */}
      <section className="py-20" style={{ backgroundColor: "#fff", backgroundImage: "url(/brand/textura_leche.png)", backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="font-hand text-xl text-[var(--lala-blue)]">cuatro etapas · una sola familia</div>
            <h2 className="font-display text-4xl md:text-5xl mt-1 text-[var(--lala-blue)]">Una trayectoria que se construye juntos.</h2>
            <p className="text-[var(--ink-soft)] mt-4">
              Cada nivel cuenta una etapa del camino: la estrella que se forma, evoluciona, asciende y brilla.
              Avanzamos junto contigo, paso a paso, hacia tu mejor versión como socio Lala.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="tiers-grid">
            {(data?.tiers || []).map((t, i) => {
              const meta = TIER_META[t.key] || {};
              return (
                <div key={t.key} className={`relative ${TIER_FRAME[t.key] || "card-silver"} p-6 rise rise-${i + 1}`} data-testid={`tier-card-${t.key}`}>
                  <div className="absolute -top-3 -right-3 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest" style={{ background: meta.color, color: "#fff" }}>
                    {t.label}
                  </div>
                  <img src={`/brand/icon_tier_${t.key}_metal.png`} alt="" className="w-14 h-14 mb-3 object-contain" data-testid={`tier-icon-${t.key}`} draggable={false} />
                  <div className="font-display text-3xl text-[var(--ink)]">{t.label}</div>
                  <div className="font-hand text-xl text-[var(--lala-blue)]">{t.range}</div>
                  {t.tagline && <div className="font-hand text-base text-[var(--ink-soft)] mt-1">{t.tagline}</div>}
                  <p className="text-sm text-[var(--ink-soft)] mt-3 leading-relaxed">{t.benefit}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why — postal: mariposas + logo LALA + globo con marco plata + sello ELITE */}
      <section className="text-white pt-16 pb-24 relative overflow-hidden" style={{ backgroundColor: "var(--lala-blue)", backgroundImage: "url(/brand/mariposa_azul_bg.png)", backgroundSize: "cover", backgroundPosition: "center" }} data-testid="why-section">
        <div className="relative z-10 flex justify-center mb-10">
          <img src="/brand/lala_blanco_trim.png" alt="Grupo LALA" className="h-14 w-auto" data-testid="why-lala-logo" draggable={false} />
        </div>
        <div className="max-w-6xl mx-auto px-6 relative z-10 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-4 flex justify-center md:justify-start">
            <div className="card-silver p-2 w-full max-w-[320px]">
              <img
                src="/brand/postal_persona.jpg"
                alt="Socio de la familia Lala Elite"
                className="rounded-xl w-full object-cover"
                data-testid="why-persona-img"
                draggable={false}
              />
            </div>
          </div>
          <div className="md:col-span-8">
            <div className="card-silver relative p-8 md:p-10" data-testid="why-quote-card" style={{ borderRadius: 28 }}>
              <div className="hidden md:block absolute top-1/2 -left-[14px] w-7 h-7 bg-white rotate-45" style={{ borderLeft: "3px solid #AEB6C4", borderBottom: "3px solid #AEB6C4" }} />
              <div className="font-hand text-xl text-[var(--lala-blue)]">por qué Lala Elite</div>
              <h2 className="font-display text-3xl md:text-4xl mt-2 text-[var(--lala-blue)]" style={{ fontFamily: "'Bolota', 'Barlow', sans-serif" }}>
                “La excelencia no se construye en solitario.”
              </h2>
              <p className="font-program mt-4 text-base md:text-lg leading-relaxed" style={{ color: "var(--lala-blue)", opacity: 0.9 }}>
                Se construye en comunidad. LALA Elite es la red donde Grupo Lala y sus proveedores estratégicos
                comparten visibilidad, confianza y ambición. Cuando todos los actores avanzamos juntos,{" "}
                <b className="italic">el impacto se expande hacia millones de familias.</b>
              </p>
              <img
                src="/brand/lala_elite_badge_md.png"
                alt="Sello LALA ELITE"
                className="absolute -bottom-10 -right-4 md:-right-8 h-28 w-28 object-contain drop-shadow-2xl"
                data-testid="why-elite-badge"
                draggable={false}
              />
            </div>
            <Link to="/login" className="inline-block mt-10 px-6 py-3 bg-white text-[var(--lala-blue)] font-semibold rounded-full hover:translate-y-[-2px] transition-transform" data-testid="why-cta">
              Entrar a la plataforma
            </Link>
          </div>
        </div>
      </section>

      {/* Valores LALA — fondo azul en continuidad · marcos plata · títulos Bolota · splash inferior */}
      <section id="valores" className="pt-20 pb-56 relative overflow-hidden" style={{ backgroundColor: "var(--lala-blue)", backgroundImage: "url(/brand/mariposa_azul_bg.png)", backgroundSize: "cover", backgroundPosition: "center top" }}>
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="font-hand text-xl" style={{ color: "#fff", opacity: 0.8 }}>los valores que compartimos</div>
            <h2 className="font-display text-4xl md:text-5xl mt-1 text-white" style={{ fontFamily: "'Bolota', 'Barlow', sans-serif" }}>
              La cultura LALA, vivida con quienes nos acompañan.
            </h2>
            <p className="text-white/80 mt-4">
              Elite no nace de la nada: nace de cómo Lala hace las cosas. Estos son los cinco valores oficiales
              que más se viven todos los días dentro del programa con cada proveedor.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="valores-grid">
            {[
              { k: "meritocracia", h: "Crecemos por Meritocracia", b: "Cada nivel se gana. Lo medimos con transparencia y los mismos criterios para todos — para que cada paso adelante sea mérito tuyo." },
              { k: "autenticos", h: "Somos Auténticos", b: "Sin cajas negras. Cada indicador, cada rúbrica y cada decisión vive a la vista — porque la confianza se construye con honestidad." },
              { k: "no-atajos", h: "No Tomamos Atajos", b: "Cuando Lala te reconoce, no es un sello cómodo: es el resultado de elegir el camino correcto, sin saltar pasos." },
              { k: "resultados", h: "Damos Resultados", b: "Avanzar significa entregar — productividad, mejoras, sostenibilidad. Premiamos el cumplimiento ambicioso que se queda y crece." },
              { k: "ejemplo", h: "Somos Ejemplo", b: "Los proveedores Elite son referencia. Inspiran a toda la cadena de valor a crecer con el mismo estándar." },
            ].map((v) => (
              <div key={v.k} className="card-silver p-5" data-testid={`valor-${v.k}`}>
                <div className="font-hand text-lg text-[var(--lala-blue)]">valor oficial Lala</div>
                <h3 className="font-display text-2xl mt-1 text-[var(--lala-blue)]">{v.h}</h3>
                <p className="text-sm text-[var(--ink-soft)] mt-2 leading-relaxed">{v.b}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <div className="font-hand text-2xl" style={{ color: "#fff" }} data-testid="lema-firma">
              Porque alimentar tu vida es nuestra pasión — y eso lo hacemos juntos.
            </div>
          </div>
        </div>
        <img aria-hidden alt="" src="/brand/splash_lala.webp" className="absolute bottom-0 left-0 w-full h-auto pointer-events-none select-none" draggable={false} data-testid="valores-splash" />
      </section>
    </div>
  );
}
